/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { OverflowCarousel } from '@coursera-web/cds-labs';

import { Typography2 as Typography } from '@coursera/cds-core';

import type { TopReview } from 'bundles/reviews-common/types/reviewsSharedTypes';
import TopReviewCarouselCard from 'bundles/reviews/components/TopReviewCarouselCard';

import _t from 'i18n!nls/reviews';

const CARD_WIDTH = 450;
const CARD_GAP = 16;

const styles = {
  section: css`
    margin: var(--cds-spacing-400) 0;
    max-width: 100%;
  `,
  sectionTitle: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  carouselContainer: css`
    [data-testid='overflow-carousel-content'] {
      display: flex;
      align-items: stretch;
      gap: ${CARD_GAP}px;
    }

    /* OverflowCarousel wraps each child in a [role="listitem"]; make it pass height down so cards match */
    [data-testid='overflow-carousel-content'] [role='listitem'] {
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  `,
  cardItem: css`
    width: ${CARD_WIDTH}px;
    flex-shrink: 0;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  `,
};

type TopReviewsSectionProps = {
  topReviews: Array<TopReview>;
};

const TopReviewsSection = ({ topReviews }: TopReviewsSectionProps) => {
  if (!topReviews || topReviews.length === 0) {
    return null;
  }

  const validReviews = topReviews.filter(
    (review) => review?.authorName && typeof review?.averageFiveStarRating === 'number'
  );

  if (validReviews.length === 0) {
    return null;
  }

  return (
    <section css={styles.section} aria-label={_t('Top reviews carousel')} data-testid="top-reviews-section">
      <Typography variant="subtitleLarge" component="h2" css={styles.sectionTitle}>
        {_t('Top reviews')}
      </Typography>
      <div css={styles.carouselContainer}>
        <OverflowCarousel
          showNavigationButtons
          shouldAllowSpaceForHoverItems={false}
          shouldStartOverflowAtMediumBreakpoint
        >
          {validReviews.map((review) => (
            <div key={review.timestamp} css={styles.cardItem}>
              <TopReviewCarouselCard topReview={review} />
            </div>
          ))}
        </OverflowCarousel>
      </div>
    </section>
  );
};

export default TopReviewsSection;
